import { Chip, Grid2 as Grid, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { ProductDetail } from '../../api/business/types';
import { formatDate } from '../../components/format';
import { LanguageTabs } from '../../components/LanguageTabs';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductAvailabilitySectionProps = {
  product: ProductDetail;
};

const Item = ({ label, children }: { label: string; children: ReactNode }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Grid>
);

/**
 * A product's availability (R0030 WP-07): what the shop says in and out of stock, per content language; the date a
 * pre-order ships; the ordering flags — each on or off at a glance.
 */
export const ProductAvailabilitySection = ({ product }: ProductAvailabilitySectionProps) => {
  const { language, t } = useLanguage();
  const { languages } = useContentLanguages();
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const current = languages.includes(active) ? active : languages[0];
  const texts = product.translations[current];
  const flags: Array<{ label: string; on: boolean }> = [
    { label: t('availability.availableForOrder'), on: product.availableForOrder },
    { label: t('availability.showPrice'), on: product.showPrice },
    { label: t('availability.onlineOnly'), on: product.onlineOnly },
    { label: t('availability.onSaleBadge'), on: product.onSaleBadge },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('availability.title')}
      </Typography>
      {languages.length > 1 ? (
        <LanguageTabs
          languages={languages}
          value={current}
          onChange={setActive}
          missing={(code) => !product.translations[code]?.availableNow}
        />
      ) : null}
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
        <Item label={t('availability.availableNow')}>{texts?.availableNow ?? t('availability.noText')}</Item>
        <Item label={t('availability.availableLater')}>
          {texts?.availableLater ?? t('availability.noText')}
        </Item>
        <Item label={t('availability.availableFrom')}>
          {product.availableFrom === null
            ? t('availability.noDate')
            : formatDate(product.availableFrom, language)}
        </Item>
      </Grid>
      <Typography variant="overline" color="text.secondary">
        {t('availability.flags')}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {flags.map((flag) => (
          <Chip
            key={flag.label}
            size="small"
            variant={flag.on ? 'filled' : 'outlined'}
            color={flag.on ? 'primary' : 'default'}
            label={`${flag.label}: ${flag.on ? t('common.yes') : t('common.no')}`}
          />
        ))}
      </Stack>
    </Paper>
  );
};
